import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { MODELS, getDecision, getTests, getOffer, getFilterSet, Availability, LEGACY, METRIC_LABEL, DecisionState } from '@/data/catalogue';
import { fmt } from '@/lib/calculator';

const STOCK: Record<Availability, string> = { 'in-stock': 'in stock', 'out-of-stock': 'out of stock', unknown: 'not confirmed' };

const STATE_LABEL: Record<DecisionState, string> = {
  eligible: 'Evidence accepted',
  held: 'Held for review',
  archived: 'Archived (owners only)',
};

function cadrSummary(id: string) {
  const ts = getTests(id).filter((t) => t.isMaxSpeed);
  if (ts.length === 0) return 'No output figure on record';
  return ts.map((t) => `${METRIC_LABEL[t.metric].replace(/ \(.*\)/, '')} ${t.cfm}`).join(' · ');
}

export default function ProductsIndex() {
  const groups: DecisionState[] = ['eligible', 'held', 'archived'];
  return (
    <>
      <Helmet>
        <title>Air purifier catalogue with sources — What Size Air Purifier</title>
        <meta name="description" content="Every air purifier record we hold, with its source, check date and status. Held and archived models are explained, not hidden." />
        <link rel="canonical" href="https://whatsizeairpurifier.com/products" />
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <h1 className="text-3xl font-bold text-foreground">Catalogue</h1>
        <p className="text-muted-foreground mt-2 max-w-3xl">
          US models only. Each number links to where it came from. "Evidence accepted" means the figures are good enough to size with — it does not mean the model is in stock. Results also require the purifier and a compatible filter to be seen in stock; the
          others are kept so you can see why. This is a small, checked list — not a "best of" ranking. Use the{' '}
          <Link to="/#calculator" className="text-primary underline">calculator</Link> to see which ones meet your room's target.
        </p>

        <div className="overflow-x-auto mt-8 rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th scope="col" className="text-left p-3">Model</th>
                <th scope="col" className="text-left p-3">Status</th>
                <th scope="col" className="text-left p-3">Output at max speed (CFM)</th>
                <th scope="col" className="text-left p-3">Rated power</th>
                <th scope="col" className="text-left p-3">Observed price</th>
                <th scope="col" className="text-left p-3">Stock when checked</th>
              </tr>
            </thead>
            <tbody>
              {groups.flatMap((g) =>
                MODELS.filter((m) => getDecision(m.id)?.state === g).map((m) => {
                  const offer = getOffer(m.id);
                  return (
                    <tr key={m.id} className="border-t border-border align-top">
                      <td className="p-3">
                        <Link to={`/products/${m.id}`} className="font-medium text-primary underline">{m.brand} {m.name}</Link>
                        <div className="text-xs text-muted-foreground">{m.sku}</div>
                      </td>
                      <td className="p-3"><Badge variant={g === 'eligible' ? 'default' : 'outline'}>{STATE_LABEL[g]}</Badge></td>
                      <td className="p-3">{cadrSummary(m.id)}</td>
                      <td className="p-3">{m.ratedPowerW != null ? `${m.ratedPowerW} W` : 'unknown'}</td>
                      <td className="p-3">{offer ? `$${fmt(offer.priceUsd, 2)} (${offer.observed})` : 'unknown'}</td>
                      <td className="p-3 text-xs">Purifier: {offer ? STOCK[offer.stock] : 'no offer'}<br />Filter: {getFilterSet(m.id) ? STOCK[getFilterSet(m.id)!.availability] : 'no record'}</td>
                    </tr>
                  );
                }),
              )}
            </tbody>
          </table>
        </div>

        <section className="mt-12">
          <h2 className="text-xl font-bold text-foreground">Removed from earlier versions of this site</h2>
          <p className="text-sm text-muted-foreground mt-1">These had numbers we could not support. Their old pages now explain what changed.</p>
          <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm">
            {LEGACY.map((l) => (
              <li key={l.id}><Link to={`/products/${l.id}`} className="text-primary underline">{l.name}</Link></li>
            ))}
          </ul>
        </section>
        <p className="text-xs text-muted-foreground mt-8">
          Spotted an error? <Link to="/corrections" className="text-primary underline">Report a correction</Link> · <Link to="/editorial" className="text-primary underline">Editorial status report</Link>
        </p>
      </main>
      <Footer />
    </>
  );
}
